import { Drawer, message } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllAssetCategories } from "../../../core/data/redux/asset/assetCategoryActions";
import { createAssetRequest, fetchAllAssetRequests, fetchAssetRequestsByEmployeeId, updateAssetRequest } from "../../../core/data/redux/asset/assetRequest/assetRequestAction";
import { filterByStatus } from "../../../core/utils/filterActiveData";

const AssetRequestModal: React.FC<any> = ({
    modalType,
    isModalVisible,
    handleCancel,
    employeeId,
    setTableKey,
    assetRequestId,
    selectedRowData,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { assetCategories } = useSelector((state: any) => state.asset.assetCategory);
    const { loadingStates } = useSelector((state: any) => state.asset.assetRequest);
    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;
    const [errors, setErrors] = useState<any>({});
    const [assetRequestData, setAssetRequestData] = useState({
        assetCategoryId: "",
        name: "",
        quantity: 1,
        requestDescription: "",
    });

    const activeAssetCategory = filterByStatus(assetCategories, "active", "status");

    useEffect(() => {
        if (!assetCategories.length) {
            dispatch(fetchAllAssetCategories());
        }
    }, [assetCategories.length, dispatch, modalType]);

    useEffect(() => {
        if (modalType === "edit" && assetRequestId && selectedRowData) {
            setAssetRequestData({
                assetCategoryId: selectedRowData.AssetCategoryId || "",
                name: selectedRowData.Name || "",
                quantity: selectedRowData.Quantity || 1,
                requestDescription: selectedRowData.RequestDescription || "",
            });
        }
        setErrors({});
    }, [selectedRowData, assetRequestId, modalType]);

    const handleInputChange = (e: any, field: string) => {
        const { value } = e.target;
        setAssetRequestData((prevState: any) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetForm = () => {
        setAssetRequestData({
            assetCategoryId: "",
            name: "",
            quantity: 1,
            requestDescription: "",
        });
        setErrors({});
    };

    const validateForm = () => {
        let formIsValid = true;
        let newErrors: any = {
            assetCategoryId: "",
            name: "",
            quantity: "",
            requestDescription: "",
        };

        if (!assetRequestData.assetCategoryId) {
            formIsValid = false;
            newErrors.assetCategoryId = "Please select an asset category.";
        }

        if (!assetRequestData.name.trim()) {
            formIsValid = false;
            newErrors.name = "Asset name is required.";
        }

        if (!assetRequestData.quantity || assetRequestData.quantity <= 0) {
            formIsValid = false;
            newErrors.quantity = "Quantity must be greater than 0.";
        }

        setErrors(newErrors);
        return formIsValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        const AssetRequestData = {
            AssetCategoryId: Number(assetRequestData.assetCategoryId),
            Name: assetRequestData.name,
            Quantity: assetRequestData.quantity,
            RequestDescription: assetRequestData.requestDescription,
            EmployeeId: employeeId,
            CreatedBy: user?.UserId,
            ModifiedBy: user?.UserId,
        };

        if (modalType === "create") {
            const result = await dispatch(createAssetRequest(AssetRequestData));
            if (result.meta.requestStatus === "fulfilled") {
                message.success("Asset request created successfully");
                resetForm();
                await dispatch(fetchAssetRequestsByEmployeeId(employeeId));
                if (typeof setTableKey === "function") {
                    setTableKey((prevKey: any) => prevKey + 1);
                }
            } else if (result.meta.requestStatus === "rejected") {
                message.error(result.payload.error);
            }
        } else if (modalType === "edit" && assetRequestId) {
            const result = await dispatch(
                updateAssetRequest({ id: assetRequestId, data: AssetRequestData })
            );
            if (result.meta.requestStatus === "fulfilled") {
                message.success("Asset request updated successfully");
                await dispatch(fetchAssetRequestsByEmployeeId(employeeId));
                if (typeof setTableKey === "function") {
                    setTableKey((prevKey: any) => prevKey + 1);
                }
                resetForm();
            } else if (result.meta.requestStatus === "rejected") {
                message.error(result.payload.error);
            }
        }
        handleCancel();
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit Asset Request" : "Add Asset Request"}
            placement="right"
            width={650}
            onClose={handleCancel}
            visible={isModalVisible}
            style={{ marginTop: 50, paddingBottom: 50 }}
        >
            <form>
                <div className="modal-body pb-0">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label">Asset Category <span className="text-danger">*</span></label>
                                <select
                                    className="form-control"
                                    value={assetRequestData?.assetCategoryId}
                                    onChange={(e) => handleInputChange(e, "assetCategoryId")}
                                >
                                    <option value="">Select Category</option>
                                    {[...activeAssetCategory]
                                        .sort((a: any, b: any) => a.category_name.localeCompare(b.category_name))
                                        .map((category: any) => (
                                            <option key={category.asset_category_id} value={category.asset_category_id}>
                                                {category.category_name}
                                            </option>
                                        ))}
                                </select>
                                {errors.assetCategoryId && (
                                    <div style={{ color: "red" }}>{errors.assetCategoryId}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label">Asset Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={assetRequestData?.name}
                                    onChange={(e) => handleInputChange(e, "name")}
                                />
                                {errors.name && (
                                    <div style={{ color: "red" }}>{errors.name}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label">Quantity <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={assetRequestData?.quantity}
                                    onChange={(e) => handleInputChange(e, "quantity")}
                                />
                                {errors.quantity && (
                                    <div style={{ color: "red" }}>{errors.quantity}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-12">
                            <div className="mb-3">
                                <label className="form-label">Request Description </label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={assetRequestData?.requestDescription}
                                    onChange={(e) => handleInputChange(e, "requestDescription")}
                                />
                                {errors.requestDescription && (
                                    <div style={{ color: "red" }}>{errors.requestDescription}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-light me-2"
                        onClick={() => {
                            handleCancel();
                            setErrors({});
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isLoading}

                    >
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update Request" : "Submit Request"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default AssetRequestModal;
